<template>
  <div class="flex flex-col">
    <div v-if="rows.length" class="rounded-lg border border-solid border-soft">
      <div class="flex w-full items-center rounded-t-lg px-4 py-2 bg-soft text-xs font-medium text-gray uppercase tracking-wide">
        <span class="w-3/12">Name</span>
        <span class="w-2/12">Type</span>
        <span class="w-6/12">Description the AI reads</span>
        <span class="w-1/12 text-center">Req.</span>
        <span class="w-8"/>
      </div>

      <div class="divide-x-0 divide-y divide-solid divide-soft">
        <div v-for="(row, index) in rows" :key="index" class="flex w-full items-center px-4 py-3">
          <div class="w-3/12 pr-3">
            <base-input v-model="row.name"
                        :data-testid="`tool-param-name-${index}`"
                        :class="{ 'ring-1 ring-focus-error rounded': duplicateNameIndexes.has(index) }"
                        class="w-full font-mono placeholder-gray"
                        placeholder="orderId"
                        @input="emitSchema"
            />
            <span v-if="duplicateNameIndexes.has(index)" class="text-xs text-focus-error">Name already used above</span>
          </div>
          <div class="w-2/12 pr-3">
            <base-select v-model="row.type"
                         :options="typeOptions"
                         :data-testid="`tool-param-type-${index}`"
                         class="w-full h-10"
                         @input="emitSchema"
            />
          </div>
          <div class="w-6/12 pr-3">
            <base-input v-model="row.description"
                        :data-testid="`tool-param-description-${index}`"
                        class="w-full placeholder-gray"
                        placeholder="The order number the customer gave, e.g. AB-10293"
                        @input="emitSchema"
            />
          </div>
          <div class="w-1/12 flex justify-center">
            <input v-model="row.required"
                   type="checkbox"
                   :data-testid="`tool-param-required-${index}`"
                   class="cursor-pointer"
                   @change="emitSchema"
            >
          </div>
          <div class="w-8 flex justify-end">
            <vx-tooltip text="Remove"
                        position="bottom"
                        class="flex items-center justify-center h-8 w-8 rounded cursor-pointer text-gray hover:text-focus-error"
                        :data-testid="`tool-param-remove-${index}`"
                        @click.native="removeRow(index)"
            >
              <delete-icon class="h-4 w-4"/>
            </vx-tooltip>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="rounded-lg border border-dashed border-soft px-4 py-6 text-center">
      <span class="text-sm text-gray">No parameters. The AI will call this tool with no input of its own.</span>
    </div>

    <base-button variant="bordered"
                 class="mt-3 self-start h-9 px-4 text-sm"
                 data-testid="tool-param-add"
                 @click="addRow"
    >
      Add parameter
    </base-button>
  </div>
</template>

<script>
import BaseButton from '@/components/base-components/BaseButton.vue'
import DeleteIcon from '@/assets/images/icons/DeleteIcon.vue'

const TYPES = ['string', 'number', 'integer', 'boolean', 'array', 'object']

export default {
  name: 'ToolParametersEditor',

  components: { BaseButton, DeleteIcon },

  props: {
    // JSON Schema object, exactly as the engine stores it and the model receives it
    value: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      typeOptions: TYPES.map(type => ({ text: type, value: type })),
      // Rows are LOCAL state seeded once from the schema, not a computed view of it. A row being
      // typed into has a half-finished (or blank) name, which cannot appear in the schema — deriving
      // the table from the schema would delete the row mid-keystroke. The parent remounts this
      // component per tool via :key, which is what re-seeds it.
      rows: toRows(this.value)
    }
  },

  computed: {
    // Two rows sharing a name collapse into one `properties` entry on emit — the table still shows
    // both, so without this the second row's definition silently vanishes with no indication why.
    duplicateNameIndexes() {
      const names = this.rows.map(row => (row.name || '').trim())
      const counts = names.reduce((map, name) => map.set(name, (map.get(name) || 0) + 1), new Map())
      return new Set(names.reduce((indexes, name, index) => {
        if (name && counts.get(name) > 1) indexes.push(index)
        return indexes
      }, []))
    }
  },

  methods: {
    addRow() {
      this.rows.push({ name: '', type: 'string', description: '', required: false })
    },

    removeRow(index) {
      this.rows.splice(index, 1)
      this.emitSchema()
    },

    // Rebuilt from scratch on every change rather than patched: a rename is a delete + an add in
    // JSON Schema terms, and rebuilding keeps `required` consistent with `properties` by
    // construction. Rows with a blank name are skipped — they exist in the table but not yet in the
    // schema, so typing a name one character at a time cannot mint "o", "or", "ord"...
    emitSchema() {
      const properties = {}
      const required = []
      this.rows.forEach(row => {
        const name = (row.name || '').trim()
        if (!name) return

        // enum/items are round-tripped but not editable here: this editor only offers name/type/
        // description/required, so a schema authored elsewhere (API, "Try it") with an enum or an
        // array's item schema must survive an unrelated edit made through this form instead of being
        // silently dropped.
        properties[name] = {
          type: row.type,
          description: row.description || '',
          ...(row.enum !== undefined ? { enum: row.enum } : {}),
          ...(row.items !== undefined ? { items: row.items } : {})
        }
        if (row.required) required.push(name)
      })

      this.$emit('input', { type: 'object', properties, required, additionalProperties: false })
    }
  }
}

function toRows(schema) {
  const properties = (schema && schema.properties) || {}
  const required = (schema && schema.required) || []
  return Object.keys(properties).map(name => ({
    name,
    type: properties[name].type || 'string',
    description: properties[name].description || '',
    required: required.includes(name),
    enum: properties[name].enum,
    items: properties[name].items
  }))
}
</script>
